import { Download } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DialogRoot,
  DialogPortal,
  DialogBackdrop,
  DialogPopup,
  DialogHeader,
  DialogTitle,
  DialogContent,
  DialogCloseButton,
  DialogFooter,
} from '../ui/dialog';
import {
  EXPENSE_CSV_HEADER,
  buildExpenseCsvRows,
  downloadCsv,
} from '../../lib/csv';
import type { Expense, Tag } from '../../types';

export interface CsvPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  expenses: Expense[];
  availableTags: Tag[];
  filename: string;
}

export function CsvPreviewDialog({
  open,
  onClose,
  expenses,
  availableTags,
  filename,
}: CsvPreviewDialogProps) {
  const rows = buildExpenseCsvRows(expenses, availableTags);

  const handleDownload = () => {
    downloadCsv(EXPENSE_CSV_HEADER, rows, filename);
    onClose();
  };

  return (
    <DialogRoot open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogPortal>
        <DialogBackdrop className="z-40" />
        <DialogPopup className="z-41 max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Pré-visualizar CSV</DialogTitle>
            <DialogCloseButton />
          </DialogHeader>
          <DialogContent>
            <p className="text-xs text-muted-foreground">
              {rows.length} {rows.length === 1 ? 'linha' : 'linhas'} serão exportadas
            </p>

            <div className="rounded-lg border border-border overflow-hidden">
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-muted">
                    <tr>
                      {EXPENSE_CSV_HEADER.map((col) => (
                        <th
                          key={col}
                          className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap"
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => (
                      <tr key={i} className="border-t border-border">
                        {row.map((field, j) => (
                          <td
                            key={j}
                            className="px-3 py-2 text-xs text-foreground whitespace-nowrap max-w-50 truncate"
                            title={field}
                          >
                            {field}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {rows.length === 0 && (
                      <tr>
                        <td
                          colSpan={EXPENSE_CSV_HEADER.length}
                          className="px-3 py-6 text-center text-xs text-muted-foreground"
                        >
                          Nenhum gasto para exportar
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </DialogContent>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="button" onClick={handleDownload} disabled={rows.length === 0}>
              <Download className="size-4" />
              Baixar CSV
            </Button>
          </DialogFooter>
        </DialogPopup>
      </DialogPortal>
    </DialogRoot>
  );
}
